/**
 * ArchitectView Component
 *
 * Professional architecture recommendations visualization with:
 * - Architecture Summary with service recommendations
 * - Compute & Database recommendations
 * - Cost Estimates with breakdown
 * - Java to AWS Traceability
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import * as d3 from 'd3'
import {
  ArrowLeft, Layout, Server, Database, DollarSign, GitBranch,
  Loader2, AlertCircle, Cloud, Cpu, HardDrive,
  Shield, Globe, CheckCircle, AlertTriangle, ArrowRight, Info,
  Zap, Lock, Key, Wifi, Box, Layers, TrendingDown,
  ChevronRight, Activity
} from 'lucide-react'
import { getArchitectureResults } from '../../services/codeAnalysisService'
import './ArchitectView.css'

/**
 * Tab configuration
 */
const TABS = {
  'summary': { label: 'Summary', icon: Layout, description: 'Architecture overview' },
  'services': { label: 'Services', icon: Cloud, description: 'Compute & Database' },
  'costs': { label: 'Cost Estimates', icon: DollarSign, description: 'Monthly costs' },
  'traceability': { label: 'Traceability', icon: GitBranch, description: 'Java to AWS mapping' },
}

const COLORS = {
  primary: '#7aa2f7',
  success: '#9ece6a',
  warning: '#e0af68',
  danger: '#f7768e',
  info: '#7dcfff',
  purple: '#bb9af7',
  aws: '#ff9900',
}

/**
 * Format a cost value without losing small nonzero amounts.
 */
function formatCost(val) {
  if (val == null || val === 0) return '$0'
  if (val < 0.005) return '<$0.01'
  if (val < 10) return `$${val.toFixed(2)}`
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`
  if (val >= 1_000) return `$${Math.round(val / 1_000)}K`
  return `$${Math.round(val).toLocaleString()}`
}

/**
 * AWS Service Icons map
 */
const AWS_ICONS = {
  'AWS Lambda': { icon: Zap, color: '#ff9900' },
  'Amazon ECS': { icon: Server, color: '#ff9900' },
  'Amazon RDS': { icon: Database, color: '#527fff' },
  'Amazon DynamoDB': { icon: Database, color: '#4053d6' },
  'Amazon Aurora': { icon: Database, color: '#527fff' },
  'API Gateway': { icon: Globe, color: '#ff4f8b' },
  'default': { icon: Cloud, color: '#8b949e' },
}

function getServiceIcon(service, size = 20) {
  const config = AWS_ICONS[service] || AWS_ICONS['default']
  const Icon = config.icon
  return <Icon size={size} style={{ color: config.color }} />
}

/**
 * Confidence Ring — visual gauge for a 0-1 confidence score
 */
function ConfidenceRing({ value, size = 80 }) {
  const pct = Math.round((value || 0) * 100)
  const color = pct >= 80 ? '#9ece6a' : pct >= 60 ? '#e0af68' : '#f7768e'
  const r = (size / 2) - 6
  const circ = 2 * Math.PI * r
  const dash = (pct / 100) * circ

  return (
    <div className="confidence-ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="#2a2f44" strokeWidth={6}
        />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="confidence-ring-label">
        <span className="confidence-ring-pct" style={{ color }}>{pct}%</span>
        <span className="confidence-ring-sub">confidence</span>
      </div>
    </div>
  )
}

/**
 * Architecture Blueprint — CSS-based flow diagram
 */
function ArchitectureBlueprint({ summary, compute, storage }) {
  const appType = summary?.application_type || ''
  const computeService = compute?.service || summary?.primary_compute || 'AWS Lambda'
  const hasStorage = summary?.primary_database && summary.primary_database !== 'None required'

  // Build a simple flow: Trigger → Compute → [Orchestration] → Output
  const nodes = [
    {
      id: 'trigger',
      label: 'COBOL Trigger',
      sub: 'Event / Schedule',
      icon: Activity,
      color: '#565f89',
      bg: 'rgba(86, 95, 137, 0.15)',
    },
    {
      id: 'compute',
      label: computeService,
      sub: appType || 'Serverless',
      icon: Zap,
      color: '#ff9900',
      bg: 'rgba(255, 153, 0, 0.12)',
    },
  ]

  if (appType?.toLowerCase().includes('event') || compute?.functions?.length > 1) {
    nodes.push({
      id: 'orchestrate',
      label: 'Step Functions',
      sub: 'Workflow orchestration',
      icon: Layers,
      color: '#7aa2f7',
      bg: 'rgba(122, 162, 247, 0.12)',
    })
  }

  if (hasStorage) {
    nodes.push({
      id: 'db',
      label: summary.primary_database,
      sub: 'Managed database',
      icon: Database,
      color: '#7dcfff',
      bg: 'rgba(125, 207, 255, 0.12)',
    })
  }

  nodes.push({
    id: 's3',
    label: 'Amazon S3',
    sub: 'Output / Storage',
    icon: HardDrive,
    color: '#9ece6a',
    bg: 'rgba(158, 206, 106, 0.12)',
  })

  return (
    <div className="arch-blueprint">
      {nodes.map((node, i) => {
        const Icon = node.icon
        return (
          <div key={node.id} className="blueprint-node-wrap">
            <div
              className="blueprint-node"
              style={{ background: node.bg, borderColor: node.color + '40' }}
            >
              <div className="blueprint-node-icon" style={{ color: node.color }}>
                <Icon size={20} />
              </div>
              <div className="blueprint-node-text">
                <span className="blueprint-node-label" style={{ color: node.color }}>
                  {node.label}
                </span>
                <span className="blueprint-node-sub">{node.sub}</span>
              </div>
            </div>
            {i < nodes.length - 1 && (
              <ChevronRight size={16} className="blueprint-arrow" />
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Cost Donut Chart (D3)
 */
function CostDonutChart({ costs }) {
  const svgRef = useRef(null)

  useEffect(() => {
    if (!svgRef.current || !costs) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const width = 180
    const height = 180
    const radius = Math.min(width, height) / 2 - 10

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`)

    const data = [
      { label: 'Compute', value: costs.compute_monthly || 0, color: COLORS.aws },
      { label: 'Database', value: costs.database_monthly || 0, color: COLORS.purple },
      { label: 'API', value: costs.api_monthly || 0, color: COLORS.warning },
      { label: 'Storage', value: costs.storage_monthly || 0, color: COLORS.info },
      { label: 'Other', value: costs.other_monthly || 0, color: '#565f89' },
    ].filter(d => d.value > 0)

    if (data.length === 0) return

    const pie = d3.pie().value(d => d.value).sort(null)
    const arc = d3.arc().innerRadius(radius * 0.6).outerRadius(radius)

    g.selectAll('path')
      .data(pie(data))
      .join('path')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', '#16161e')
      .attr('stroke-width', 2)

    g.append('text')
      .attr('text-anchor', 'middle').attr('dy', -5)
      .attr('fill', '#c0caf5').attr('font-size', 18).attr('font-weight', 700)
      .text(formatCost(costs.total_monthly || 0))

    g.append('text')
      .attr('text-anchor', 'middle').attr('dy', 14)
      .attr('fill', '#565f89').attr('font-size', 11)
      .text('/month')
  }, [costs])

  return <svg ref={svgRef} />
}

/* ══════════════════════════════════════════
   SUMMARY TAB
══════════════════════════════════════════ */
function SummaryTab({ data }) {
  if (!data?.summary) {
    return (
      <div className="empty-state">
        <p>No architecture summary available</p>
        <span>Run architecture analysis to generate recommendations</span>
      </div>
    )
  }

  const summary = data.summary
  const validation = data.validation_report || {}
  const compute = data.compute_recommendation

  const appTypeDescriptions = {
    'event-driven': 'Processes events asynchronously using triggers and reactive handlers',
    'batch': 'Executes scheduled bulk processing jobs on large data sets',
    'api': 'Exposes functionality through REST or SOAP APIs',
    'streaming': 'Processes continuous data streams in real time',
  }
  const appType = (summary.application_type || '').toLowerCase()
  const appTypeDesc = appTypeDescriptions[appType] || 'Cloud-native application pattern'

  return (
    <div className="architecture-summary">

      {/* ── Metric Cards ── */}
      <div className="primary-recommendations">
        <div className="recommendation-card">
          <div className="rec-icon aws-orange">
            <Zap size={22} />
          </div>
          <div className="rec-content">
            <label>Primary Compute</label>
            <span className="rec-value">{summary.primary_compute || 'Not determined'}</span>
          </div>
        </div>

        <div className="recommendation-card">
          <div className="rec-icon aws-purple">
            <Database size={22} />
          </div>
          <div className="rec-content">
            <label>Primary Database</label>
            <span className="rec-value">{summary.primary_database || 'None required'}</span>
          </div>
        </div>

        <div className="recommendation-card">
          <div className="rec-icon aws-blue">
            <Globe size={22} />
          </div>
          <div className="rec-content">
            <label>API Required</label>
            <span className="rec-value">{summary.api_required ? 'Yes' : 'No'}</span>
          </div>
        </div>

        <div className="recommendation-card">
          <div className="rec-icon aws-green">
            <DollarSign size={22} />
          </div>
          <div className="rec-content">
            <label>Monthly Cost</label>
            <span className="rec-value">{formatCost(summary.estimated_monthly_cost)}</span>
          </div>
        </div>
      </div>

      {/* ── Architecture Blueprint ── */}
      <div className="arch-section">
        <h4><Layers size={16} /> Target Architecture Blueprint</h4>
        <ArchitectureBlueprint
          summary={summary}
          compute={compute}
        />
      </div>

      {/* ── App Type + Confidence ── */}
      <div className="app-confidence-row">
        <div className="app-type-section">
          <h4><Info size={16} /> Application Type</h4>
          <div className="app-type-display">
            <div className="app-type-badge">{summary.application_type || 'Unknown'}</div>
            <p className="app-type-desc">{appTypeDesc}</p>
          </div>
        </div>
        <div className="confidence-section">
          <h4>Overall Confidence</h4>
          <div className="confidence-display">
            <ConfidenceRing value={summary.overall_confidence || 0} size={90} />
            <p className="confidence-note">
              Based on analysis of COBOL source structure and discovered business patterns
            </p>
          </div>
        </div>
      </div>

      {/* ── Validation Warnings ── */}
      {validation.warnings?.length > 0 && (
        <div className="warnings-section">
          <h4><AlertTriangle size={16} /> Validation Warnings</h4>
          <div className="warnings-list">
            {validation.warnings.map((warning, i) => (
              <div key={i} className="warning-card">
                <div className="warning-card-header">
                  <AlertTriangle size={14} className="warning-icon" />
                  <span className="warning-type">
                    {typeof warning === 'string'
                      ? warning
                      : warning.warning_type?.replace(/_/g, ' ') || warning.description || 'Unknown warning'}
                  </span>
                </div>
                {warning.sources && (
                  <div className="warning-sources">
                    {Object.entries(warning.sources).map(([src, detail]) => (
                      <div key={src} className="warning-source-row">
                        <span className="warning-source-key">{src}</span>
                        <span className="warning-source-val">{detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Data Sources ── */}
      {data.sources_used?.length > 0 && (
        <div className="sources-section">
          <h4><CheckCircle size={16} /> Data Sources Used</h4>
          <div className="sources-list">
            {data.sources_used.map((source, i) => (
              <span key={i} className="source-badge">
                <CheckCircle size={11} />
                {source}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════
   SERVICES TAB
══════════════════════════════════════════ */
function ServicesTab({ data }) {
  const compute = data?.compute_recommendation
  const database = data?.database_recommendation
  const api = data?.api_recommendation
  const security = data?.security_recommendation

  if (!compute && !database) {
    return (
      <div className="empty-state">
        <p>No service recommendations available</p>
      </div>
    )
  }

  // Group security features into categories
  const securityCategories = security ? [
    {
      label: 'Network',
      icon: Wifi,
      color: '#7aa2f7',
      items: [
        { key: 'VPC', enabled: security.vpc_required },
        { key: 'Private Subnets', enabled: security.private_subnets },
        { key: 'WAF', enabled: security.waf_required },
      ],
    },
    {
      label: 'Data Protection',
      icon: Lock,
      color: '#9ece6a',
      items: [
        { key: 'Encryption at Rest', enabled: security.encryption_at_rest },
        { key: 'Encryption in Transit', enabled: security.encryption_in_transit },
      ],
    },
    {
      label: 'Access Control',
      icon: Key,
      color: '#e0af68',
      items: [
        { key: 'Secrets Manager', enabled: security.secrets_manager },
        { key: 'IAM Roles', enabled: true },
      ],
    },
  ] : []

  return (
    <div className="services-view">

      {/* Compute */}
      {compute && (
        <div className="service-section">
          <h3><Zap size={18} /> Compute Recommendation</h3>
          <div className="service-card featured">
            <div className="service-card-top">
              <div className="service-hero-icon aws-orange-bg">
                <Zap size={28} />
              </div>
              <div className="service-card-title">
                <div className="service-name-row">
                  <span className="service-name">{compute.service}</span>
                  <span
                    className="confidence-pill"
                    style={{
                      color: (compute.confidence || 0) >= 0.8 ? '#9ece6a' : '#e0af68',
                      borderColor: (compute.confidence || 0) >= 0.8 ? 'rgba(158,206,106,0.3)' : 'rgba(224,175,104,0.3)',
                      background: (compute.confidence || 0) >= 0.8 ? 'rgba(158,206,106,0.1)' : 'rgba(224,175,104,0.1)',
                    }}
                  >
                    {Math.round((compute.confidence || 0) * 100)}% confidence
                  </span>
                </div>
                <p className="service-rationale">{compute.rationale}</p>
              </div>
            </div>

            {compute.functions?.length > 0 && (
              <div className="functions-container">
                <div className="functions-header">
                  <Zap size={13} />
                  Lambda Functions ({compute.functions.length})
                </div>
                <div className="functions-grid">
                  {compute.functions.map((func, i) => (
                    <div key={i} className="function-card">
                      <div className="func-card-name">{func.name}</div>
                      <div className="func-card-meta">
                        {func.trigger && (
                          <span className="func-trigger-badge">{func.trigger}</span>
                        )}
                        {func.source_class && (
                          <span className="func-source-label">{func.source_class}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Database */}
      {database && database.service !== 'None' && (
        <div className="service-section">
          <h3><Database size={18} /> Database Recommendation</h3>
          <div className="service-card">
            <div className="service-card-top">
              <div className="service-hero-icon aws-purple-bg">
                <Database size={28} />
              </div>
              <div className="service-card-title">
                <div className="service-name-row">
                  <span className="service-name">{database.service}</span>
                  <span className="confidence-pill">
                    {Math.round((database.confidence || 0) * 100)}% confidence
                  </span>
                </div>
                <p className="service-rationale">{database.rationale}</p>
              </div>
            </div>

            {database.sizing && (
              <div className="sizing-grid">
                {database.sizing.instance_class && (
                  <div className="sizing-card">
                    <label>Instance Class</label>
                    <span>{database.sizing.instance_class}</span>
                  </div>
                )}
                {database.sizing.storage_gb && (
                  <div className="sizing-card">
                    <label>Storage</label>
                    <span>{database.sizing.storage_gb} GB</span>
                  </div>
                )}
                {database.sizing.read_replicas > 0 && (
                  <div className="sizing-card">
                    <label>Read Replicas</label>
                    <span>{database.sizing.read_replicas}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API */}
      {api?.required && (
        <div className="service-section">
          <h3><Globe size={18} /> API Gateway</h3>
          <div className="service-card">
            <div className="service-card-top">
              <div className="service-hero-icon aws-pink-bg">
                <Globe size={28} />
              </div>
              <div className="service-card-title">
                <div className="service-name-row">
                  <span className="service-name">Amazon API Gateway</span>
                </div>
                <p className="service-rationale">{api.rationale}</p>
              </div>
            </div>

            {api.endpoints?.length > 0 && (
              <div className="endpoints-container">
                <div className="endpoints-header">
                  <Globe size={13} />
                  Endpoints ({api.endpoints.length})
                </div>
                {api.endpoints.slice(0, 5).map((ep, i) => (
                  <div key={i} className="endpoint-row">
                    <span className="ep-method">{ep.method}</span>
                    <span className="ep-path">{ep.path}</span>
                  </div>
                ))}
                {api.endpoints.length > 5 && (
                  <div className="more-label">+{api.endpoints.length - 5} more endpoints</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Security */}
      {security && (
        <div className="service-section">
          <h3><Shield size={18} /> Security Configuration</h3>
          <div className="security-categories">
            {securityCategories.map(cat => {
              const CatIcon = cat.icon
              const enabledCount = cat.items.filter(i => i.enabled).length
              return (
                <div key={cat.label} className="security-category">
                  <div
                    className="security-cat-header"
                    style={{ borderColor: cat.color + '40' }}
                  >
                    <CatIcon size={14} style={{ color: cat.color }} />
                    <span style={{ color: cat.color }}>{cat.label}</span>
                    <span className="security-cat-count">
                      {enabledCount}/{cat.items.length}
                    </span>
                  </div>
                  <div className="security-items">
                    {cat.items.map(item => (
                      <div
                        key={item.key}
                        className={`security-item-card ${item.enabled ? 'enabled' : 'disabled'}`}
                      >
                        <CheckCircle size={13} />
                        <span>{item.key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════
   COST ESTIMATES TAB
══════════════════════════════════════════ */
function CostEstimatesTab({ data }) {
  const costs = data?.cost_estimate

  if (!costs) {
    return (
      <div className="empty-state">
        <p>No cost estimates available</p>
      </div>
    )
  }

  const computeMonthly = costs.compute_monthly ?? costs.compute_cost?.monthly_cost ?? 0
  const databaseMonthly = costs.database_monthly ?? costs.database_cost?.monthly_cost ?? 0
  const apiMonthly = costs.api_monthly ?? costs.api_cost?.monthly_cost ?? 0
  const storageMonthly = costs.storage_monthly ?? costs.storage_cost?.monthly_cost ?? 0
  const totalMonthly = costs.total_monthly ?? (computeMonthly + databaseMonthly + apiMonthly + storageMonthly)
  const totalAnnual = costs.total_annual ?? (totalMonthly * 12)

  const normalizedCosts = {
    compute_monthly: computeMonthly,
    database_monthly: databaseMonthly,
    api_monthly: apiMonthly,
    storage_monthly: storageMonthly,
    total_monthly: totalMonthly,
  }

  // Legacy comparison — if we have discovery ROI data context, or derive from known mainframe cost
  const legacyAnnual = 50000 // known from ROI analysis
  const savingsAnnual = legacyAnnual - totalAnnual
  const savingsPct = legacyAnnual > 0 ? Math.round((savingsAnnual / legacyAnnual) * 100) : 0

  return (
    <div className="cost-estimates">

      {/* ── Cost Hero + Breakdown ── */}
      <div className="cost-hero-row">
        <div className="cost-hero-left">
          <div className="cost-hero-card">
            <DollarSign size={28} className="cost-hero-icon" />
            <div className="cost-hero-amount">{formatCost(totalMonthly)}</div>
            <div className="cost-hero-label">per month</div>
            <div className="cost-hero-annual">{formatCost(totalAnnual)}/year</div>
          </div>
        </div>
        <div className="cost-hero-right">
          <h4>Monthly Cost Breakdown</h4>
          <div className="cost-items">
            {computeMonthly > 0 && (
              <div className="cost-item">
                <div className="cost-label">
                  <span className="cost-dot" style={{ background: COLORS.aws }} />
                  Compute (Lambda)
                </div>
                <span className="cost-value">{formatCost(computeMonthly)}</span>
              </div>
            )}
            {databaseMonthly > 0 && (
              <div className="cost-item">
                <div className="cost-label">
                  <span className="cost-dot" style={{ background: COLORS.purple }} />
                  Database
                </div>
                <span className="cost-value">{formatCost(databaseMonthly)}</span>
              </div>
            )}
            {apiMonthly > 0 && (
              <div className="cost-item">
                <div className="cost-label">
                  <span className="cost-dot" style={{ background: COLORS.warning }} />
                  API Gateway
                </div>
                <span className="cost-value">{formatCost(apiMonthly)}</span>
              </div>
            )}
            {storageMonthly > 0 && (
              <div className="cost-item">
                <div className="cost-label">
                  <span className="cost-dot" style={{ background: COLORS.info }} />
                  Storage
                </div>
                <span className="cost-value">{formatCost(storageMonthly)}</span>
              </div>
            )}
            <div className="cost-item total">
              <span>Total Monthly</span>
              <span className="cost-value total">{formatCost(totalMonthly)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── vs Legacy comparison ── */}
      <div className="vs-legacy-section">
        <h4><TrendingDown size={16} /> Cost vs Legacy Mainframe</h4>
        <div className="vs-legacy-bars">
          <div className="vs-bar-row">
            <span className="vs-bar-label">Legacy Mainframe</span>
            <div className="vs-bar-track">
              <div className="vs-bar-fill legacy" style={{ width: '100%' }}>
                <span className="vs-bar-value">{formatCost(legacyAnnual)}/yr</span>
              </div>
            </div>
          </div>
          <div className="vs-bar-row">
            <span className="vs-bar-label">AWS Cloud</span>
            <div className="vs-bar-track">
              <div
                className="vs-bar-fill aws"
                style={{ width: `${Math.max(1, 100 - savingsPct)}%` }}
              >
                <span className="vs-bar-value">{formatCost(totalAnnual)}/yr</span>
              </div>
            </div>
          </div>
        </div>
        <div className="vs-savings-badge">
          <TrendingDown size={13} />
          {savingsPct}% annual cost reduction — save {formatCost(savingsAnnual)}/year
        </div>
      </div>

      {/* ── Annual Projection ── */}
      <div className="annual-projection">
        <h4>Annual Cost Projection</h4>
        <div className="projection-grid">
          <div className="projection-item current">
            <label>Year 1</label>
            <span>{formatCost(totalAnnual)}</span>
            <div className="proj-note">Current estimate</div>
          </div>
          <div className="projection-item">
            <label>Year 2 <span className="growth-note">+20% growth</span></label>
            <span>{formatCost(totalAnnual * 1.2)}</span>
          </div>
          <div className="projection-item">
            <label>Year 3 <span className="growth-note">+40% growth</span></label>
            <span>{formatCost(totalAnnual * 1.4)}</span>
          </div>
        </div>
      </div>

      {/* ── Assumptions ── */}
      {costs.assumptions && Object.keys(costs.assumptions).length > 0 && (
        <div className="assumptions-section">
          <h4>Assumptions</h4>
          <ul>
            {Object.entries(costs.assumptions).map(([key, value]) => (
              <li key={key}>
                <strong>{key.replace(/_/g, ' ')}:</strong> {value}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════
   TRACEABILITY TAB
══════════════════════════════════════════ */
function TraceabilityTab({ data }) {
  const traceability = data?.traceability
  const mappings = traceability?.mappings || []

  if (mappings.length === 0) {
    // Enhanced empty state explaining what traceability means and when it's available
    return (
      <div className="traceability-empty">
        <div className="trace-empty-icon">
          <GitBranch size={40} />
        </div>
        <h3>Java-to-AWS Traceability</h3>
        <p>Traceability mappings are generated after the Java transformation (Code Refactor) is complete.</p>
        <p>Once generated, this view will show each Java class mapped to its target AWS service.</p>

        <div className="trace-preview">
          <div className="trace-preview-title">What you'll see here:</div>
          <div className="trace-preview-items">
            <div className="trace-preview-item">
              <div className="trace-preview-java">
                <span className="trace-mono">CustomerProcessor.java</span>
                <span className="trace-mini-badge java">Java</span>
              </div>
              <ArrowRight size={14} className="trace-arrow" />
              <div className="trace-preview-aws">
                <Zap size={13} style={{ color: '#ff9900' }} />
                <span className="trace-mono" style={{ color: '#ff9900' }}>
                  process-customer Lambda
                </span>
              </div>
            </div>
            <div className="trace-preview-item">
              <div className="trace-preview-java">
                <span className="trace-mono">TransactionBatch.java</span>
                <span className="trace-mini-badge java">Java</span>
              </div>
              <ArrowRight size={14} className="trace-arrow" />
              <div className="trace-preview-aws">
                <Layers size={13} style={{ color: '#7aa2f7' }} />
                <span className="trace-mono" style={{ color: '#7aa2f7' }}>
                  batch-workflow StepFunctions
                </span>
              </div>
            </div>
            <div className="trace-preview-item">
              <div className="trace-preview-java">
                <span className="trace-mono">ReportGenerator.java</span>
                <span className="trace-mini-badge java">Java</span>
              </div>
              <ArrowRight size={14} className="trace-arrow" />
              <div className="trace-preview-aws">
                <HardDrive size={13} style={{ color: '#9ece6a' }} />
                <span className="trace-mono" style={{ color: '#9ece6a' }}>
                  reports-bucket S3
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="trace-steps">
          <div className="trace-step done">
            <CheckCircle size={15} />
            Architecture analysis complete
          </div>
          <div className="trace-step pending">
            <div className="trace-step-dot" />
            Complete Java transformation (Code Refactor)
          </div>
          <div className="trace-step pending">
            <div className="trace-step-dot" />
            Re-run Architecture to generate mappings
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="traceability-view">
      <div className="mapping-header">
        <div className="header-col source">Java Class</div>
        <div className="header-col arrow" />
        <div className="header-col target">AWS Target</div>
      </div>

      <div className="mapping-list">
        {mappings.map((mapping, i) => (
          <div key={i} className="mapping-item">
            <div className="mapping-source">
              <span className="class-name">{mapping.java_class}</span>
              {mapping.java_package && (
                <span className="package-name">{mapping.java_package}</span>
              )}
            </div>
            <div className="mapping-arrow">
              <ArrowRight size={16} />
            </div>
            <div className="mapping-target">
              <span className="service-name">{mapping.aws_service}</span>
              <span className="resource-name">{mapping.aws_resource_name}</span>
              {mapping.entry_type && (
                <span className="entry-type">{mapping.entry_type}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {traceability.unmapped_classes?.length > 0 && (
        <div className="unmapped-section">
          <h4><AlertTriangle size={16} /> Unmapped Classes</h4>
          <div className="unmapped-list">
            {traceability.unmapped_classes.map((cls, i) => (
              <span key={i} className="unmapped-class">{cls}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
function ArchitectView({ onBack, analysisContext }) {
  const [activeTab, setActiveTab] = useState('summary')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const jobId = analysisContext?.jobs?.architecture_job_id || analysisContext?.jobId

  useEffect(() => {
    const fetchData = async () => {
      if (!jobId) { setLoading(false); return }
      setLoading(true)
      setError(null)
      try {
        const result = await getArchitectureResults(jobId)
        setData(result)
      } catch (err) {
        console.error('Failed to fetch architecture results:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [jobId])

  const handleRefresh = useCallback(async () => {
    setLoading(true)
    try {
      if (jobId) {
        const result = await getArchitectureResults(jobId)
        setData(result)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [jobId])

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Loader2 size={32} className="spin" />
          <p>Loading architecture recommendations...</p>
        </div>
      )
    }
    if (error) {
      return (
        <div className="error-state">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )
    }
    switch (activeTab) {
      case 'summary': return <SummaryTab data={data} />
      case 'services': return <ServicesTab data={data} />
      case 'costs': return <CostEstimatesTab data={data} />
      case 'traceability': return <TraceabilityTab data={data} />
      default: return null
    }
  }

  return (
    <div className="architect-view">
      <div className="view-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back
        </button>
        <div className="header-title">
          <Layout size={20} />
          <h2>Architecture Recommendations</h2>
        </div>
        <div></div>
      </div>

      <div className="tab-nav">
        {Object.entries(TABS).map(([key, tab]) => {
          const Icon = tab.icon
          return (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default ArchitectView
